/**
 * Cross-tab client-refresh listener.
 *
 * When another tab (or the legacy Angular shell during canary) posts to the
 * `clientRefresh_channel` BroadcastChannel, we mirror the legacy behaviour:
 *   - update the active client to match the remote tab (silent — no re-post),
 *   - refetch the client list when the remote client is unknown here so a
 *     just-granted (JIT) client is added before we switch,
 *   - clear MSAL cache so the next token call rotates.
 */

import { markTokenRotationOnNextCall } from '@/auth/auth-module';
import { queryClient } from '@/http/query-client';
import {
  subscribeClientRefresh,
  subscribeFavoriteToggle,
  type ClientRefreshPayload,
  type FavoriteTogglePayload,
} from './broadcast';
import { useClientStore, type ClientInfo } from './client-store';
import { CLIENTS_QUERY_KEY } from './use-clients';
import { navigateToClientScope } from '@/router/client-navigation';

let installed = false;

export function installCrossTabClientRefresh(): void {
  if (installed) return;
  installed = true;

  subscribeClientRefresh((msg) => {
    void applyRemoteClientRefresh(msg.data);
  });

  subscribeFavoriteToggle((msg) => {
    applyRemoteFavoriteToggle(msg.data);
  });
}

/**
 * Apply a client switch broadcast from another tab. If the remote client is not
 * in this tab's cached list (an orchestrator task can GRANT a brand-new client
 * that wasn't present when this tab loaded — JIT), refetch the RUM client list
 * first so the new client is added to the dropdown before we switch. Mirrors the
 * legacy receiver, which calls EY_RAPToR_PortalRefresh() (a full refresh that
 * refetches clients), but without the second MFE remount / style flash — we only
 * refetch the list, then navigate once.
 */
async function applyRemoteClientRefresh(data: ClientRefreshPayload['data']): Promise<void> {
  const { clientId } = data;
  let available = useClientStore.getState().available;
  let remote = available.find((c) => c.id === clientId);

  if (!remote) {
    try {
      await queryClient.refetchQueries({ queryKey: CLIENTS_QUERY_KEY });
      available =
        (queryClient.getQueryData(CLIENTS_QUERY_KEY) as ClientInfo[] | undefined) ?? available;
      useClientStore.getState().setAvailable(available);
      remote = available.find((c) => c.id === clientId);
    } catch {
      /* best-effort: fall through with resolved:false (legacy no-ops here too) */
    }
  }

  const state = useClientStore.getState();

  if (remote && state.current?.id !== remote.id) {
    // silent — do NOT re-post (loop-safe with the senderId filter); and we never
    // call EY_RAPToR_PortalRefresh here (that would double-remount the MFE and
    // flash styles — the whole reason this receiver stays lightweight).
    state.setCurrent(remote, { silent: true, source: 'reset' });
    navigateToClientScope(remote.id);
  }

  markTokenRotationOnNextCall();
}

/**
 * Apply a favorite toggle broadcast from another tab. Updates the Zustand store
 * immediately (so the dropdown re-renders with the correct star state) and
 * invalidates the React Query client-list cache so the next open/focus picks up
 * any server-side ordering changes.
 */
function applyRemoteFavoriteToggle(data: FavoriteTogglePayload['data']): void {
  const { clientId, favorite } = data;
  useClientStore.getState().toggleFavorite(clientId, favorite);
  void queryClient.invalidateQueries({ queryKey: CLIENTS_QUERY_KEY });
}
